<script setup>
// Imports
import { ref, watch, onMounted, computed } from 'vue';
import axios from '@/plugins/axios'
import { Cropper, CircleStencil } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

// Components and composables
import Toast from '@/components/toast.vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import { useToast } from '@/composables/use-toast';
import { useStorage } from '@/composables/use-storage';
import StripeButtonConnect from '@/pages/stripe/StripeButtonConnect.vue';

// Setup components and composables
const { TextInput, InputError, PrimaryButton, ButtonLoading, SelectInput } = useLayoutComponents();
const { User } = useIcons();
const { addToast } = useToast();
const { saveUserInfo, userInfo, userAuthToken, hasFlag } = useStorage();

onMounted(async () => {
    try {
        const response = await axios.get(`/api/users/${userInfo.value.id}`, {
            headers: {
                'Authorization': `Bearer ${userAuthToken.value}`
            }
        });
        
        if (response.data && response.data.onboarding_fields) {
            form.value.onboarding_fields = response.data.onboarding_fields;
        }
    } catch (error) {
        console.error('Error fetching user data:', error);
    }
    fetchCountries();
});

// Form data
const form = ref({
    id: userInfo.value?.id || null,
    username: userInfo.value?.username || '',
    name: userInfo.value?.name || '',
    email: userInfo.value?.email || '',
    profile_pic: localStorage.getItem('profilePicture') || '',
    profile_info: userInfo.value?.profile_info || '',
    user_type: userInfo.value?.user_type || '',
    base_of_operations: userInfo.value?.base_of_operations || '',
    huggingface_username: userInfo.value?.socials?.find(s => s.name === 'huggingface')?.value || '',
    github_username: userInfo.value?.socials?.find(s => s.name === 'github')?.value || '',
    x_username: userInfo.value?.socials?.find(s => s.name === 'x')?.value || '',
    homepage: userInfo.value?.socials?.find(s => s.name === 'homepage')?.value || '',
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
    country: userInfo.value?.country || '',
    postal_code: userInfo.value?.postal_code || '',
    onboarding_fields: userInfo.value?.onboarding_fields || {},
});

watch(() => userInfo.value, (newUserInfo) => {
    if (newUserInfo) {
        form.value = {
            ...form.value,
            id: newUserInfo.id || null,
            username: newUserInfo.username || '',
            name: newUserInfo.name || '',
            email: newUserInfo.email || '',
            profile_pic: localStorage.getItem('profilePicture') || '',
            profile_info: newUserInfo.profile_info || '',
            user_type: newUserInfo.user_type || '',
            base_of_operations: newUserInfo.base_of_operations || '',
            huggingface_username: newUserInfo.socials?.find(s => s.name === 'huggingface')?.value || '',
            github_username: newUserInfo.socials?.find(s => s.name === 'github')?.value || '',
            x_username: newUserInfo.socials?.find(s => s.name === 'x')?.value || '',
            homepage: newUserInfo.socials?.find(s => s.name === 'homepage')?.value || '',
            onboarding_fields: newUserInfo.onboarding_fields || {},
        };
    }
}, { deep: true });

// Profile image handling
const showCropper = ref(false);
const imageSource = ref('');
const cropperResult = ref(null);
const profilePicture = localStorage.getItem('profilePicture');
const profileImagePreview = ref(form.value.profile_pic || '');
const showPreview = ref(false);
const showDefaultAvatar = ref(false);

// Form validation errors
const usernameError = ref('');
const nameError = ref('');
const emailError = ref('');
const currentPasswordError = ref('');
const newPasswordError = ref('');
const newPasswordConfirmationError = ref('');
const profileInfoError = ref('');
const baseOfOperationsError = ref('');
const userTypeError = ref('');
const loading = ref(false);
const cropperLoading = ref(false);

const accountType = ref(userInfo.value?.account_type || '');
const isAdmin = ref(userInfo.value?.is_admin || false);

const uploadProfilePicture = async () => {
    try {
        const response = await axios.post(`/api/users/${userInfo.value.id}/profile-picture`, form.value.profile_pic, {
            headers: {
                'Authorization': `Bearer ${userAuthToken.value}`
            }
        });

        if (response.status === 200 || response.status === 201) {
            addToast(response.data.message, 'success');
            
            const updatedUserInfo = {
                ...userInfo.value,
                profile_pic: response.data.profile_pic
            };
            saveUserInfo(updatedUserInfo);

            window.dispatchEvent(new CustomEvent('profile-updated', {
                detail: { profilePicture: response.data.profile_pic }
            }));

            localStorage.setItem('profilePicture', response.data.profile_pic);

            return true;
        }
        
        if (response.data.error) {
            addToast(response.data.message, 'error');
            return false;
        }
    } catch (error) {
        return false;
    }
};

const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {

        if (file.size > 5 * 1024 * 1024) {
            addToast('File size must be less than 5MB', 'error');
            return;
        }
        showDefaultAvatar.value = false;

        const reader = new FileReader();
        reader.onload = (e) => {
            imageSource.value = e.target.result;
            showCropper.value = true;
        };
        reader.readAsDataURL(file);
    }
};

const onCropperChange = ({ canvas }) => {
    cropperResult.value = canvas;
};

const saveCroppedImage = async () => {
    if (cropperResult.value) {
        cropperLoading.value = true;

        const maxSize = 200;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const ratio = Math.min(maxSize / cropperResult.value.width, maxSize / cropperResult.value.height);

        canvas.width = cropperResult.value.width * ratio;
        canvas.height = cropperResult.value.height * ratio;

        ctx.drawImage(cropperResult.value, 0, 0, canvas.width, canvas.height);

        const base64Image = canvas.toDataURL('image/jpeg', 0.6);

        form.value.profile_pic = base64Image.toString();
        
        const uploadSuccess = await uploadProfilePicture();
        
        if (uploadSuccess) {
            profileImagePreview.value = base64Image;
            showPreview.value = true;
            showDefaultAvatar.value = false;
            showCropper.value = false;
        }

        cropperLoading.value = false;
    }
};

const cancelCropping = () => {
    showCropper.value = false;
    const fileInput = document.getElementById('profile_image');
    if (fileInput) fileInput.value = '';
};

const validateUsernameInput = () => {
    const { username } = form.value;

    if (!username) {
        usernameError.value = 'Username is required!';
        return false;
    }
    if (username.length < 3 || username.length > 20) {
        usernameError.value = 'Username must be between 3 and 20 characters!';
        return false;
    }
    if (!/^[a-zA-Z0-9][\w]*[a-zA-Z0-9]$/.test(username)) {
        usernameError.value = 'Username can only contain letters, numbers, and underscores (not at start/end)!';
        return false;
    }
    return true;
};

const validateNameInput = () => {
    const { name } = form.value;

    if (!name) {
        nameError.value = 'Name is required!';
        return false;
    }
    if (name.length > 255) {
        addToast('Name must be less than 255 characters!', 'error');
        return false;
    }
    return true;
};

const validateEmailInput = () => {
    const { email } = form.value;

    if (!email) {
        emailError.value = 'Email is required!';
        return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        emailError.value = 'Please enter a valid email address!';
        return false;
    }
    return true;
};

const validatePasswordFields = () => {
    const { current_password, new_password, new_password_confirmation } = form.value;
    currentPasswordError.value = '';
    newPasswordError.value = '';
    newPasswordConfirmationError.value = '';

    if (!current_password && !new_password && !new_password_confirmation) {
        return true;
    }

    let isValid = true;

    if (!current_password) {
        currentPasswordError.value = 'Current password is required!';
        isValid = false;
    }

    if (new_password) {
        if (new_password.length < 8) {
            newPasswordError.value = 'Password must be at least 8 characters!';
            isValid = false;
        }

        if (new_password !== new_password_confirmation) {
            newPasswordConfirmationError.value = 'Passwords do not match!';
            isValid = false;
        }
    } else if (current_password) {
        newPasswordError.value = 'New password is required!';
        isValid = false;
    }

    if (new_password_confirmation && !new_password) {
        newPasswordError.value = 'New password is required!';
        isValid = false;
    }

    return isValid;
};

const validateProfileInfoInput = () => {
    const { profile_info } = form.value;

    if (!profile_info) {
        profileInfoError.value = 'Profile info is required!';
        return false;
    }
    if (profile_info.length < 10) {
        profileInfoError.value = 'Please provide at least 10 characters of information!';
        return false;
    }
    return true;
};

const validateUserTypeInput = () => {
    const { user_type } = form.value;

    if (user_type && user_type.length < 3) {
        userTypeError.value = 'If provided, please write at least 3 characters';
        return false;
    }
    return true;
};

// Form submission
const sendRequest = async () => {
    // Reset all errors
    usernameError.value = '';
    nameError.value = '';
    emailError.value = '';
    currentPasswordError.value = '';
    newPasswordError.value = '';
    newPasswordConfirmationError.value = '';
    profileInfoError.value = '';
    baseOfOperationsError.value = '';
    userTypeError.value = '';

    // Validate all inputs
    const isUsernameValid = validateUsernameInput();
    const isNameValid = validateNameInput();
    const isEmailValid = validateEmailInput();
    const isPasswordValid = validatePasswordFields();
    const isProfileInfoValid = validateProfileInfoInput();
    const isUserTypeValid = validateUserTypeInput();

    if (!isUsernameValid || !isNameValid || !isEmailValid || !isPasswordValid || !isProfileInfoValid || !isUserTypeValid) {
        return;
    }

    loading.value = true;

        const updateData = {
            username: form.value.username,
            name: form.value.name,
            email: form.value.email,
            profile_info: form.value.profile_info,
            user_type: form.value.user_type,
            base_of_operations: form.value.base_of_operations,
            country: form.value.country,
            postal_code: form.value.postal_code,
            socials: [
                { name: 'huggingface', value: form.value.huggingface_username || null },
                { name: 'github', value: form.value.github_username || null },
                { name: 'x', value: form.value.x_username || null },
                { name: 'homepage', value: form.value.homepage || null }
            ],
            onboarding_fields: form.value.onboarding_fields || {}
        };

        if (form.value.current_password || form.value.new_password || form.value.new_password_confirmation) {
            if (!validatePasswordFields()) {
                return;
            }
            updateData.current_password = form.value.current_password;
            updateData.new_password = form.value.new_password;
            updateData.new_password_confirmation = form.value.new_password_confirmation;
        }

        const getAuthHeaders = () => {
            const token = userAuthToken.value;
            return {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            };
        };

        const response = await axios.post(`/api/users/${userInfo.value.id}`, updateData, getAuthHeaders());

        if (!response.data.error) {
            const { userInfos } = response.data;

            const updatedUserInfo = {
                ...userInfo.value,
                username: userInfos.username || userInfo.value?.username || '',
                name: userInfos.name || userInfo.value?.name || '',
                email: userInfos.email || userInfo.value?.email || '',
                profile_info: userInfos.profile_info || userInfo.value?.profile_info || '',
                user_type: userInfos.user_type || userInfo.value?.user_type || '',
                base_of_operations: userInfos.base_of_operations !== undefined ? userInfos.base_of_operations : (userInfo.value?.base_of_operations || ''),
                socials: userInfos.socials || userInfo.value?.socials || [],
                country: userInfos.country || userInfo.value?.country || '',
                postal_code: userInfos.postal_code || userInfo.value?.postal_code || '',
            };

            form.value.current_password = '';
            form.value.new_password = '';
            form.value.new_password_confirmation = '';

            if (accountType.value === 'seller' || isAdmin.value) {
                updatedUserInfo.seller = {
                    ...(userInfo.value?.seller || {}),
                };
            }

            form.value = { ...updatedUserInfo };
            saveUserInfo(updatedUserInfo);

            if (userInfos.profile_pic) {
                profileImagePreview.value = profilePicture;
                showPreview.value = true;
                showDefaultAvatar.value = false;

                window.dispatchEvent(new CustomEvent('profile-updated', {
                    detail: { profilePicture }
                }));
            } else {
                profileImagePreview.value = '';
                localStorage.removeItem('profilePicture');
                showPreview.value = false;
                showDefaultAvatar.value = true;

                window.dispatchEvent(new CustomEvent('profile-updated', {
                    detail: { profilePicture: null }
                }));
            }

            addToast(response.data.message, 'success');
            loading.value = false;
        } else {
            addToast(response.data.message, 'error');
            loading.value = false;
        }
    
};

const cropperRef = ref(null);

const createStripeAccount = async () => {
    const response = await axios.post('/api/sellers/create-stripe-account/' + userInfo.value.id, {
        email: userInfo.value.email,
    });
    if (response.data.error) {
        addToast(response.data.msg, 'error');
    } else {
        const currentUserInfo = userInfo.value;
        currentUserInfo.seller.stripe_account_id = response.data;
        saveUserInfo(currentUserInfo);
        addToast('Stripe account created successfully!', 'success');
    }
};

const canConnectStripe = computed(() => {
    return hasFlag('stripe-payment') && userInfo.value.seller && userInfo.value.seller.stripe_account_id === '';
});

const countries = ref([]);
const postalCodeError = ref('');
import postalCodePatterns from '@/data/postalCodePatterns.json';

const fetchCountries = async () => {
    const response = await axios.get('https://restcountries.com/v3.1/all?fields=name,cca2');
    countries.value = response.data.map(country => ({
        label: country.name.common,
        value: country.cca2
    }));
}

const validatePostalCode = () => {
    const postalCode = document.getElementById('postal_code').value;
    const selectedCountry = form.value.country;
    postalCodeError.value = '';

    if (!postalCode) {
        postalCodeError.value = 'Postal code is required';
        return;
    }

    if (!selectedCountry) {
        postalCodeError.value = 'Please select a country first';
        return;
    }

    const pattern = postalCodePatterns[selectedCountry];
    if (pattern) {
        const regex = new RegExp('^' + pattern + '$');
        if (!regex.test(postalCode)) {
            postalCodeError.value = 'Invalid postal code format for selected country';
        }
    }
}

</script>
<template>
    <div class="creator-profile">
        <Toast />
      
        <div v-if="showCropper" class="cropper-modal" data-modal>
            <div class="cropper-container">
                <div class="modal__header">
                    <h2 class="modal__title">Crop Your Profile Image</h2>
                    <button class="modal__close" @click="cancelCropping">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="modal__body">
                    <Cropper ref="cropperRef" :src="imageSource" @change="onCropperChange"
                        :stencil-component="CircleStencil" :stencil-props="{
                            aspectRatio: 1,
                            movable: true,
                            resizable: true,
                            handlers: {
                                eastNorth: true,
                                north: true,
                                westNorth: true,
                                west: true,
                                westSouth: true,
                                south: true,
                                eastSouth: true,
                                east: true
                            },
                            handlersClasses: {
                                default: 'handler-default'
                            },
                            lineWidth: 2
                        }" class="cropper" />
                </div>
                <div class="modal__footer">
                    <button class="btn btn--secondary" @click="cancelCropping">Cancel</button>
                    <PrimaryButton @click="saveCroppedImage" :disabled="cropperLoading">
                        <ButtonLoading v-if="cropperLoading" size="xs" text="Saving..." />
                        <template v-else>Save</template>
                    </PrimaryButton>
                </div>
            </div>
        </div>
        <div class="creator-profile__hero p-5">
           
            <div class="py-[24px] pb-0 border-b border-echoes-border-gray mb-6 flex flex-col">
                <div class="creator-profile__header">
                    <div class="creator-profile__avatar-container pb-6">
                        <div class="profile-photo">
                            <div class="profile-image-preview"
                                :class="{ 'has-image': profileImagePreview || (profilePicture && !showDefaultAvatar) }">
                                <img v-if="profilePicture && !showPreview && !showDefaultAvatar" :src="profilePicture"
                                    alt="profile photo" class="preview-image">
                                <img v-else-if="profileImagePreview && showPreview && !showDefaultAvatar"
                                    :src="profileImagePreview" alt="profile photo" class="preview-image">
                                <User v-else class="default-avatar" />
                            </div>
                            <div class="profile-photo__actions">
                                <input type="file" id="profile_image" accept="image/*" @change="handleImageUpload"
                                    class="hidden" />
                                <label for="profile_image" class="profile-photo__upload-btn">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" id="upload"><path fill="currentColor" d="M8.71,7.71,11,5.41V15a1,1,0,0,0,2,0V5.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42l-4-4a1,1,0,0,0-.33-.21,1,1,0,0,0-.76,0,1,1,0,0,0-.33.21l-4,4A1,1,0,1,0,8.71,7.71ZM21,12a1,1,0,0,0-1,1v6a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V13a1,1,0,0,0-2,0v6a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V13A1,1,0,0,0,21,12Z"></path></svg>
                                </label>
                            </div>
                        </div>
                        <p class="profile-photo__help">
                            Square JPG, PNG, at least 200x200 pixels.
                        </p>
                    </div>

                    <div class="creator-profile__info">
                        <div class="creator-profile__name-row">
                            <h1 class="creator-profile__title">Account Settings</h1>
                        </div>

                        <p class="creator-profile__subtitle">Manage your account information and security settings</p>
                    </div>
                    <div v-if="canConnectStripe"><StripeButtonConnect/></div>
                </div>
            </div>
        </div>

        <div class="creator-profile__container">

            <div class="gap-6 pb-3 hidden" v-if="userInfo.seller">
                <div class="settings-card" v-if="userInfo.seller.stripe_account_id !== ''">
                    <div class="text-red-500 mb-3" v-if="!userInfo.seller.stripe_payout_enabled">Your stripe account
                        need to be completed to receive credit card, Apple Pay, Google Pay and other payments</div>
                    <div class="text-green-500 mb-3" v-else>Your stripe account is completed and ready to receive
                        payments.</div>
                    <a :href="'https://connect.stripe.com/app/express#' + userInfo.seller.stripe_account_id"
                        target="_blank" class="btn btn--secondary">
                        <span v-if="!userInfo.seller.stripe_payout_enabled">Complete your stripe account</span>
                        <span v-else>View your stripe account</span>
                    </a>
                </div>
                <div v-else>
                    <button class="btn btn--secondary" @click="createStripeAccount">Create your stripe account</button>
                </div>
            </div>
            <form class="settings-form" @submit.prevent="sendRequest" novalidate>
                <div class="settings-form__grid">
                    <div class="settings-card">
                        <h3>Personal Information</h3>
                        <div class="form-group">
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <div class="field-label-container">
                                        <label class="input-label mb-0" for="username">Username</label>
                                    </div>
                                    <TextInput id="username" v-model="form.username" type="text"
                                        placeholder="Username" disabled />
                                </div>
                                <div>
                                    <div class="field-label-container">
                                        <label class="input-label mb-0" for="name">Name<span
                                                class="required-mark">*</span></label>
                                        <InputError :message="nameError" />
                                    </div>
                                    <TextInput id="name" v-model="form.name" type="text"
                                        placeholder="Full name or organization name" />
                                </div>
                            </div>
                        </div>

                        <div class="form-group mt-6">
                            <div class="field-label-container">
                                <label class="input-label mb-0" for="email">Email<span
                                        class="required-mark">*</span></label>
                                <InputError :message="emailError" />
                            </div>
                            <TextInput id="email" v-model="form.email" type="email" placeholder="user@example.com" />
                        </div>
                        <div class="form-group mt-6">
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <div class="field-label-container">
                                        <label class="input-label mb-0" for="country">Country</label>
                                    </div>
                                    <div class="relative"> 
                                        <SelectInput
                                            v-model="form.country"
                                            :options="countries" 
                                            id="country"
                                            placeholder="Select country" 
                                            class="w-full"
                                        ></SelectInput>
                                    </div>
                                </div>

                                <div>
                                    <div class="field-label-container">
                                        <label class="input-label mb-0" for="postal_code">Postal Code</label>
                                    </div>
                                    <div class="relative">
                                        <TextInput type="text" id="postal_code" placeholder="Postal Code" class="w-full" 
                                        @input="validatePostalCode()" :disabled="!form.country" v-model="form.postal_code"/>
                                    </div>
                                    <p v-if="postalCodeError" class="text-red-500 text-xs 2xl:text-sm mt-1">{{ postalCodeError }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="settings-card">
                        <h3>Profile Information</h3>
                        <div class="form-group">
                            <div class="field-label-container">
                                <label class="input-label mb-0" for="profile_info">Profile Info<span
                                        class="required-mark">*</span></label>
                                <InputError :message="profileInfoError" />
                            </div>
                            <p class="text-xs 2xl:text-sm text-slate-400 mb-2">Please tell us what best describes you: are you a professional data seller, enthusiast, scientist, or creator? Feel free to share a bit more about yourself. This is especially helpful if you are a data seller.</p>
                            <textarea class="text-area__field h-48" id="profile_info" v-model="form.profile_info"
                            placeholder="Share a bit about yourself..." />
                        </div>

                    
                    </div>

                    <div class="settings-card">
                        <h3>Social Media & Location</h3>
                        <div class="form-group">
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

                                <div>
                                    <div class="field-label-container">


                                        <label class="input-label" for="github_username">
                                            <div class="flex items-center gap-1.5">
                                                <svg class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                                    aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                                                    fill="currentColor" viewBox="0 0 24 24">
                                                    <path fill-rule="evenodd"
                                                        d="M12.006 2a9.847 9.847 0 0 0-6.484 2.44 10.32 10.32 0 0 0-3.393 6.17 10.48 10.48 0 0 0 1.317 6.955 10.045 10.045 0 0 0 5.4 4.418c.504.095.683-.223.683-.494 0-.245-.01-1.052-.014-1.908-2.78.62-3.366-1.21-3.366-1.21a2.711 2.711 0 0 0-1.11-1.5c-.907-.637.07-.621.07-.621.317.044.62.163.885.346.266.183.487.426.647.71.135.253.318.476.538.655a2.079 2.079 0 0 0 2.37.196c.045-.52.27-1.006.635-1.37-2.219-.259-4.554-1.138-4.554-5.07a4.022 4.022 0 0 1 1.031-2.75 3.77 3.77 0 0 1 .096-2.713s.839-.275 2.749 1.05a9.26 9.26 0 0 1 5.004 0c1.906-1.325 2.74-1.05 2.74-1.05.37.858.406 1.828.101 2.713a4.017 4.017 0 0 1 1.029 2.75c0 3.939-2.339 4.805-4.564 5.058a2.471 2.471 0 0 1 .679 1.897c0 1.372-.012 2.477-.012 2.814 0 .272.18.592.687.492a10.05 10.05 0 0 0 5.388-4.421 10.473 10.473 0 0 0 1.313-6.948 10.32 10.32 0 0 0-3.39-6.165A9.847 9.847 0 0 0 12.007 2Z"
                                                        clip-rule="evenodd" />
                                                </svg>
                                                GitHub
                                            </div>
                                        </label>
                                    </div>
                                    <TextInput id="github_username" v-model="form.github_username"
                                        placeholder="Your username" class="w-full" />
                                </div>
                                <div>
                                    <div class="field-label-container">


                                        <label class="input-label" for="x_username">
                                            <div class="flex items-center gap-1.5">
                                                <svg class="w-4 h-4 text-slate-800 dark:text-slate-100"
                                                    aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                                                    fill="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        d="M13.795 10.533 20.68 2h-3.073l-5.255 6.517L7.69 2H1l7.806 10.91L1.47 22h3.074l5.705-7.07L15.31 22H22l-8.205-11.467Zm-2.38 2.95L9.97 11.464 4.36 3.627h2.31l4.528 6.317 1.443 2.02 6.018 8.409h-2.31l-4.934-6.89Z" />
                                                </svg>
                                                (Twitter)
                                            </div>
                                        </label>
                                    </div>
                                    <TextInput id="x_username" v-model="form.x_username" placeholder="Your username"
                                        class="w-full" />
                                </div>
                                <div>
                                    <div class="field-label-container">

                                        <label class="input-label" for="huggingface_username">
                                            <div class="flex items-center gap-1.5">
                                                <svg class="h-5 w-5" viewBox="0 0 256 256" fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                                  
                                                    <path
                                                        d="M221.784 183.816C222.786 182.312 223.458 180.613 223.756 178.831C224.053 177.048 223.97 175.223 223.512 173.475C222.848 170.952 221.476 168.854 219.615 167.347C220.512 165.873 221.1 164.233 221.344 162.525C221.881 158.811 220.648 155.103 217.874 152.079C215.716 149.726 212.662 148.431 209.282 148.431C208.889 148.431 208.489 148.452 208.081 148.492C210.643 140.304 211.942 131.774 211.933 123.195C211.933 76.5231 174.097 38.6851 127.424 38.6851C80.75 38.6851 42.9099 76.5191 42.9099 123.195C42.9015 131.752 44.1936 140.261 46.742 148.43H46.6519C43.2719 148.43 40.219 149.724 38.06 152.077C35.287 155.098 34.0529 158.81 34.5899 162.523C34.8346 164.231 35.4231 165.872 36.3199 167.346C34.4579 168.852 33.086 170.95 32.422 173.473C31.9642 175.222 31.8817 177.047 32.1799 178.83C32.4781 180.612 33.1501 182.312 34.1519 183.816C33.9739 184.094 33.8099 184.381 33.6549 184.676C31.9849 187.847 31.877 191.43 33.352 194.767C35.588 199.824 41.1419 203.808 51.9289 208.085C58.6359 210.745 64.779 212.446 64.833 212.461C73.705 214.762 81.729 215.931 88.675 215.931C100.081 215.931 108.591 212.811 114.026 206.647C123.222 208.106 132.594 208.052 141.773 206.489C147.201 212.757 155.76 215.931 167.262 215.931C174.208 215.931 182.232 214.762 191.103 212.461C191.158 212.446 197.298 210.745 204.008 208.085C214.795 203.808 220.35 199.824 222.585 194.767C224.059 191.43 223.952 187.847 222.281 184.676C222.129 184.379 221.961 184.091 221.784 183.816ZM110.137 196.997C109.669 197.815 109.168 198.614 108.635 199.391C107.23 201.448 105.382 203.02 103.237 204.188C99.1369 206.424 93.947 207.205 88.675 207.205C80.346 207.205 71.808 205.256 67.023 204.015C66.787 203.954 37.689 195.735 41.373 188.739C41.993 187.562 43.0129 187.092 44.2979 187.092C49.4849 187.092 58.9299 194.816 62.9889 194.816C63.8959 194.816 64.5359 194.43 64.7969 193.488C66.5269 187.284 38.5039 184.676 40.8639 175.692C41.2799 174.102 42.41 173.456 43.998 173.456C50.856 173.455 66.248 185.516 69.467 185.516C69.714 185.516 69.8909 185.443 69.9869 185.291C70.0009 185.268 70.015 185.246 70.028 185.222C71.539 182.727 70.6719 180.913 60.3209 174.573L59.3269 173.968C47.9359 167.074 39.9409 162.925 44.4879 157.975C45.0109 157.404 45.7529 157.151 46.6539 157.151C47.7219 157.151 49.0149 157.508 50.4389 158.108C56.4549 160.645 64.793 167.564 68.276 170.581C68.8239 171.057 69.3683 171.538 69.9089 172.022C69.9089 172.022 74.319 176.608 76.985 176.608C77.599 176.608 78.1199 176.366 78.4729 175.768C80.364 172.58 60.9099 157.838 59.8129 151.755C59.0689 147.634 60.3349 145.546 62.6749 145.546C63.7879 145.546 65.1459 146.02 66.6449 146.971C71.2949 149.922 80.2729 165.35 83.5599 171.352C84.6619 173.363 86.5429 174.213 88.2379 174.213C91.6009 174.213 94.2299 170.87 88.5459 166.622C80.0029 160.23 83.001 149.782 87.078 149.139C87.252 149.111 87.4279 149.097 87.6029 149.097C91.3109 149.097 92.9459 155.486 92.9459 155.486C92.9459 155.486 97.7399 167.524 105.975 175.753C113.447 183.222 114.491 189.351 110.137 196.997ZM136.766 198.407L136.339 198.458L135.611 198.541C135.228 198.581 134.844 198.619 134.459 198.654L134.084 198.688L133.741 198.717L133.255 198.756L132.718 198.795L132.182 198.83L132.063 198.838C131.923 198.846 131.783 198.855 131.641 198.862L131.462 198.872C131.296 198.881 131.13 198.889 130.962 198.896L130.381 198.921L129.854 198.939L129.502 198.949H129.323C129.213 198.949 129.104 198.955 128.994 198.956H128.82C128.71 198.956 128.601 198.956 128.491 198.961L128.043 198.967H127.418C126.927 198.967 126.437 198.962 125.949 198.952L125.553 198.943C125.44 198.943 125.327 198.938 125.216 198.934L124.796 198.922L124.275 198.902L123.805 198.881L123.684 198.876L123.237 198.853C123.112 198.846 122.989 198.84 122.865 198.831L122.576 198.814C122.213 198.791 121.85 198.766 121.487 198.738L121.107 198.707C120.947 198.695 120.787 198.68 120.628 198.666C120.441 198.65 120.254 198.632 120.067 198.614C119.754 198.585 119.441 198.553 119.128 198.519H119.113C123.683 188.324 121.372 178.802 112.137 169.575C106.08 163.526 102.051 154.594 101.215 152.633C99.5229 146.828 95.045 140.375 87.608 140.375C86.979 140.375 86.351 140.425 85.73 140.523C82.472 141.036 79.624 142.911 77.592 145.733C75.396 143.002 73.262 140.831 71.332 139.605C68.422 137.76 65.5179 136.824 62.6889 136.824C59.1579 136.824 56.0019 138.274 53.8019 140.904L53.7459 140.971C53.7039 140.798 53.6639 140.625 53.6229 140.451L53.6179 140.428C53.1992 138.638 52.8477 136.833 52.5639 135.016C52.5639 135.004 52.5639 134.992 52.5579 134.98C52.5359 134.843 52.5159 134.705 52.4949 134.568C52.4334 134.162 52.3757 133.755 52.3219 133.348C52.2979 133.163 52.2719 132.978 52.2489 132.793L52.1809 132.238C52.1589 132.053 52.1409 131.885 52.1209 131.709L52.115 131.665C52.0351 130.945 51.9651 130.225 51.9049 129.503L51.8829 129.226L51.8479 128.754C51.8379 128.625 51.8279 128.495 51.8209 128.365C51.8209 128.334 51.8159 128.304 51.8149 128.275C51.7895 127.913 51.7678 127.55 51.7499 127.187C51.7399 126.998 51.7299 126.81 51.7219 126.62L51.7019 126.124L51.6969 125.974L51.6809 125.517L51.6709 125.128C51.6709 124.973 51.6629 124.818 51.6609 124.663C51.6579 124.508 51.6539 124.338 51.6529 124.174C51.6509 124.01 51.6529 123.848 51.6479 123.685C51.6439 123.521 51.6479 123.358 51.6479 123.195C51.6479 81.3421 85.5789 47.4111 127.436 47.4111C169.292 47.4111 203.222 81.3411 203.222 123.195V124.174C203.222 124.337 203.217 124.501 203.214 124.663C203.214 124.798 203.208 124.931 203.204 125.068C203.204 125.188 203.199 125.309 203.195 125.425C203.195 125.578 203.186 125.731 203.181 125.884V125.896L203.16 126.427C203.153 126.582 203.147 126.738 203.139 126.893L203.134 127.003L203.107 127.499C203.048 128.562 202.967 129.623 202.866 130.683V130.696C202.849 130.87 202.832 131.044 202.813 131.218L202.768 131.629L202.679 132.433L202.628 132.84L202.565 133.319C202.542 133.493 202.519 133.668 202.493 133.841C202.467 134.036 202.438 134.23 202.409 134.424L202.34 134.883L202.258 135.403C202.23 135.576 202.2 135.748 202.168 135.92C202.135 136.093 202.109 136.265 202.079 136.437C202.019 136.781 201.956 137.125 201.89 137.468C201.789 137.981 201.686 138.493 201.58 139.005L201.47 139.512C201.434 139.681 201.395 139.851 201.357 140.02C199.224 137.947 196.399 136.818 193.284 136.818C190.457 136.818 187.55 137.753 184.641 139.598C182.711 140.824 180.578 142.996 178.381 145.726C176.346 142.904 173.498 141.029 170.242 140.516C169.621 140.418 168.993 140.368 168.364 140.368C160.925 140.368 156.45 146.821 154.757 152.626C153.917 154.587 149.887 163.519 143.825 169.577C134.596 178.775 132.268 188.254 136.766 198.407ZM215.007 177.998L214.977 178.087C214.901 178.288 214.813 178.484 214.714 178.674C214.639 178.814 214.558 178.95 214.47 179.082C214.303 179.331 214.12 179.569 213.921 179.793C213.875 179.845 213.831 179.897 213.779 179.948C213.707 180.025 213.634 180.101 213.559 180.175C212.213 181.509 210.161 182.679 207.841 183.752C207.578 183.871 207.311 183.99 207.042 184.11L206.774 184.229C206.595 184.308 206.416 184.386 206.228 184.463C206.049 184.541 205.863 184.619 205.677 184.695L205.119 184.925C203.814 185.462 202.477 185.974 201.173 186.479L200.615 186.696L200.064 186.912C199.697 187.055 199.335 187.198 198.979 187.341L198.448 187.555L197.926 187.768L197.67 187.876C197.499 187.947 197.332 188.018 197.165 188.089C193.328 189.736 190.567 191.411 191.147 193.489C191.163 193.548 191.181 193.604 191.201 193.659C191.253 193.813 191.324 193.958 191.413 194.095C191.465 194.176 191.525 194.253 191.592 194.323C192.274 195.032 193.515 194.92 195.08 194.357C195.3 194.276 195.519 194.192 195.736 194.104L195.872 194.048C196.23 193.896 196.609 193.726 196.996 193.542C197.093 193.496 197.191 193.452 197.289 193.401C199.203 192.465 201.372 191.205 203.524 190.058C204.385 189.593 205.258 189.152 206.142 188.733C208.18 187.774 210.096 187.094 211.636 187.094C212.359 187.094 212.997 187.242 213.529 187.582L213.618 187.641C213.952 187.876 214.232 188.178 214.441 188.528C214.482 188.595 214.522 188.666 214.561 188.739C215.322 190.184 214.685 191.68 213.194 193.147C211.763 194.556 209.537 195.937 207.007 197.215C206.819 197.31 206.631 197.405 206.44 197.498C198.91 201.196 189.049 203.981 188.912 204.016C186.284 204.697 182.526 205.591 178.292 206.26L177.666 206.358L177.563 206.373C177.089 206.445 176.614 206.512 176.138 206.574C175.655 206.639 175.167 206.698 174.676 206.753L174.586 206.763C172.806 206.968 171.019 207.104 169.228 207.169H169.202C168.554 207.192 167.907 207.204 167.259 207.204H166.512C165.524 207.191 164.538 207.146 163.553 207.07C163.53 207.07 163.505 207.07 163.482 207.064C163.129 207.037 162.777 207.004 162.425 206.965C162.06 206.926 161.696 206.882 161.333 206.833C161.094 206.801 160.856 206.765 160.618 206.726C160.376 206.687 160.134 206.647 159.893 206.605L159.564 206.543L159.539 206.538C159.192 206.472 158.847 206.399 158.503 206.319C158.303 206.274 158.104 206.23 157.907 206.176L157.788 206.146C157.69 206.122 157.595 206.096 157.498 206.07L157.445 206.056L157.137 205.966C157.025 205.935 156.913 205.901 156.801 205.868L156.762 205.857L156.471 205.768C156.361 205.734 156.251 205.698 156.142 205.662L155.874 205.573L155.677 205.504C155.487 205.437 155.298 205.368 155.111 205.296L154.933 205.226L154.786 205.168C154.502 205.054 154.22 204.935 153.941 204.81L153.756 204.72L153.725 204.706C153.659 204.675 153.594 204.644 153.528 204.617C153.399 204.555 153.271 204.491 153.144 204.426L153.105 204.407L152.921 204.31C152.594 204.139 152.274 203.957 151.96 203.764L151.788 203.658C151.702 203.605 151.616 203.55 151.532 203.494L151.308 203.346L151.067 203.18L150.923 203.077C150.771 202.969 150.622 202.857 150.476 202.742L150.243 202.563C150.15 202.488 150.058 202.412 149.967 202.335C149.89 202.272 149.815 202.206 149.74 202.14L149.734 202.135C149.653 202.064 149.574 201.993 149.495 201.92C149.417 201.849 149.339 201.777 149.263 201.704L149.254 201.695C149.174 201.619 149.096 201.542 149.019 201.463C148.942 201.385 148.863 201.307 148.788 201.227C148.713 201.148 148.636 201.067 148.562 200.984C148.488 200.902 148.42 200.827 148.35 200.746L148.327 200.719C148.259 200.641 148.192 200.562 148.126 200.481C147.983 200.31 147.844 200.135 147.71 199.956C147.575 199.776 147.443 199.592 147.314 199.405L147.191 199.221C147.027 198.981 146.867 198.739 146.712 198.493C146.596 198.316 146.483 198.138 146.373 197.957C146.302 197.844 146.234 197.73 146.166 197.618L146.138 197.572C146.073 197.462 146.009 197.354 145.947 197.245C145.911 197.186 145.877 197.127 145.845 197.066C145.812 197.004 145.774 196.941 145.739 196.878L145.682 196.779L145.647 196.715C145.58 196.595 145.514 196.474 145.45 196.352C145.42 196.298 145.391 196.244 145.36 196.192L145.271 196.019L145.181 195.848C144.956 195.398 144.743 194.942 144.543 194.48L144.472 194.311C144.426 194.198 144.383 194.086 144.337 193.975C144.315 193.921 144.293 193.868 144.274 193.814C144.167 193.537 144.067 193.257 143.975 192.975C143.942 192.874 143.91 192.775 143.88 192.675C143.808 192.448 143.743 192.219 143.685 191.988C143.614 191.719 143.551 191.448 143.498 191.175C143.487 191.12 143.476 191.065 143.467 191.012C143.415 190.745 143.373 190.476 143.34 190.206C143.332 190.153 143.326 190.1 143.32 190.047L143.303 189.885C143.281 189.673 143.264 189.46 143.254 189.247C143.254 189.193 143.249 189.139 143.247 189.087C143.242 188.981 143.24 188.875 143.239 188.769C143.183 184.496 145.345 180.388 149.968 175.767C158.203 167.54 162.997 155.501 162.997 155.501C162.997 155.501 163.126 154.996 163.394 154.269C163.431 154.168 163.47 154.064 163.514 153.955C163.67 153.548 163.846 153.148 164.041 152.758L164.08 152.683C164.246 152.351 164.428 152.027 164.624 151.712C164.67 151.639 164.714 151.567 164.765 151.494C164.912 151.277 165.067 151.065 165.23 150.86C165.319 150.749 165.416 150.639 165.513 150.532C165.552 150.49 165.59 150.448 165.631 150.408C166.108 149.915 166.653 149.513 167.27 149.299L167.348 149.273C167.4 149.256 167.452 149.24 167.505 149.225C167.566 149.209 167.627 149.195 167.69 149.182L167.719 149.176C167.849 149.15 167.981 149.133 168.114 149.124H168.125C168.194 149.124 168.264 149.117 168.335 149.117C168.424 149.117 168.507 149.117 168.594 149.126C168.684 149.134 168.773 149.144 168.863 149.158C169.605 149.276 170.311 149.718 170.919 150.4C171.15 150.66 171.358 150.94 171.54 151.236C171.66 151.428 171.773 151.631 171.88 151.845C171.923 151.934 171.964 152.016 172.004 152.104C172.108 152.33 172.202 152.56 172.284 152.795C172.479 153.345 172.626 153.911 172.723 154.487C172.807 154.992 172.857 155.502 172.873 156.013C172.881 156.286 172.881 156.563 172.873 156.842C172.819 158.14 172.553 159.421 172.086 160.634C172.044 160.745 171.997 160.857 171.952 160.969C171.86 161.195 171.759 161.417 171.65 161.634C171.569 161.799 171.484 161.965 171.392 162.13C171.332 162.24 171.269 162.35 171.206 162.46C171.045 162.734 170.871 163.006 170.684 163.277L170.571 163.439C170.129 164.055 169.637 164.633 169.099 165.167C168.569 165.698 168.001 166.189 167.4 166.637C166.798 167.083 166.233 167.577 165.711 168.114C164.208 169.691 163.858 171.083 164.196 172.138C164.25 172.304 164.321 172.465 164.407 172.617C164.508 172.791 164.628 172.951 164.764 173.097L164.817 173.152L164.871 173.206C164.925 173.258 164.982 173.309 165.043 173.359L165.103 173.407C165.248 173.519 165.402 173.619 165.563 173.707C165.61 173.732 165.652 173.757 165.705 173.781C165.879 173.866 166.058 173.939 166.242 173.998C166.293 174.015 166.344 174.03 166.396 174.046L166.461 174.063L166.551 174.087L166.628 174.106L166.712 174.124L166.795 174.141L166.874 174.154C166.932 174.164 166.992 174.174 167.052 174.181L167.109 174.19L167.213 174.2L167.277 174.207L167.382 174.214H167.444L167.554 174.22H167.9L167.999 174.214L168.113 174.207L168.252 174.194L168.382 174.179C168.412 174.179 168.442 174.171 168.472 174.165C168.872 174.107 169.264 174.001 169.639 173.849L169.798 173.782C169.887 173.743 169.977 173.702 170.059 173.658C170.235 173.57 170.406 173.47 170.57 173.361C170.799 173.211 171.015 173.043 171.217 172.858C171.265 172.815 171.312 172.769 171.358 172.725C171.381 172.703 171.403 172.682 171.425 172.658C171.469 172.613 171.514 172.569 171.558 172.52C171.878 172.168 172.155 171.78 172.383 171.363C174.34 167.804 176.391 164.298 178.534 160.849L178.828 160.378L179.125 159.907C179.273 159.668 179.423 159.433 179.572 159.199L179.722 158.965C180.22 158.185 180.726 157.41 181.241 156.641L181.546 156.185C182.158 155.278 182.768 154.396 183.373 153.558L183.674 153.143C184.332 152.236 185.017 151.348 185.728 150.482L186.01 150.144C186.057 150.088 186.1 150.032 186.151 149.978C186.244 149.868 186.337 149.761 186.428 149.657C186.474 149.604 186.517 149.552 186.566 149.5L186.834 149.198L186.968 149.051C187.103 148.906 187.235 148.767 187.365 148.634C187.455 148.544 187.538 148.455 187.624 148.371C188.131 147.853 188.69 147.388 189.293 146.985L189.433 146.895C189.567 146.805 189.706 146.721 189.848 146.645C192.212 145.303 194.169 145.204 195.296 146.331C195.978 147.013 196.356 148.144 196.335 149.718C196.335 149.787 196.335 149.857 196.33 149.929V150.006C196.33 150.078 196.324 150.15 196.318 150.223C196.318 150.313 196.308 150.402 196.299 150.492C196.29 150.581 196.285 150.649 196.276 150.729C196.276 150.751 196.272 150.774 196.268 150.798C196.262 150.867 196.253 150.938 196.243 151.009C196.243 151.03 196.243 151.052 196.235 151.074C196.224 151.169 196.21 151.263 196.194 151.357C196.183 151.447 196.168 151.531 196.152 151.619L196.126 151.768C196.1 151.91 196.067 152.05 196.026 152.188C195.948 152.447 195.854 152.7 195.743 152.946C195.588 153.284 195.417 153.613 195.229 153.933C195.125 154.111 195.018 154.286 194.907 154.459C194.793 154.638 194.673 154.819 194.549 155.002C194.233 155.454 193.905 155.897 193.564 156.33L193.408 156.527C192.852 157.22 192.278 157.899 191.686 158.562L191.499 158.772C191.247 159.053 190.991 159.336 190.729 159.62L190.532 159.834C190.401 159.977 190.264 160.12 190.132 160.264C190.001 160.407 189.864 160.552 189.726 160.697L189.315 161.13L188.898 161.566L188.478 162.002C188.196 162.294 187.913 162.586 187.628 162.878C183.573 167.037 179.301 171.182 177.855 173.766C177.758 173.934 177.671 174.108 177.593 174.285C177.387 174.755 177.301 175.157 177.36 175.482C177.379 175.589 177.416 175.691 177.471 175.785C177.552 175.926 177.651 176.056 177.766 176.172C177.819 176.224 177.875 176.272 177.934 176.316C178.232 176.528 178.591 176.637 178.957 176.627H179.071L179.188 176.618L179.305 176.605L179.402 176.591C179.415 176.589 179.429 176.587 179.442 176.583L179.531 176.566L179.554 176.561L179.653 176.54L179.688 176.531C179.723 176.522 179.757 176.513 179.792 176.503C179.827 176.493 179.875 176.48 179.917 176.466C180.093 176.413 180.265 176.35 180.434 176.278C180.523 176.242 180.61 176.203 180.696 176.161C180.741 176.141 180.786 176.12 180.828 176.098L180.962 176.032C181.282 175.866 181.594 175.685 181.898 175.491L182.031 175.401C182.076 175.373 182.121 175.344 182.164 175.312L182.297 175.223L182.368 175.174L182.56 175.039C182.739 174.916 182.906 174.789 183.075 174.66L183.09 174.648L183.359 174.44C183.726 174.15 184.074 173.858 184.39 173.583L184.6 173.399L184.619 173.381L184.729 173.284C184.987 173.052 185.217 172.836 185.408 172.658L185.487 172.581C185.556 172.516 185.619 172.455 185.676 172.403L185.788 172.292L185.828 172.253L185.839 172.242L185.956 172.125L186.03 172.048L186.039 172.041L186.074 172.009L186.118 171.969L186.132 171.956L186.169 171.922L186.373 171.743L186.487 171.641C186.548 171.588 186.607 171.534 186.666 171.479L186.802 171.358C186.827 171.338 186.851 171.316 186.876 171.294L187.019 171.169L187.229 170.984L187.341 170.887C187.776 170.509 188.305 170.052 188.913 169.537L189.162 169.326L189.573 168.981L189.994 168.63C190.544 168.173 191.136 167.688 191.762 167.185L192.173 166.855C192.523 166.576 192.882 166.292 193.246 166.006C193.393 165.891 193.542 165.776 193.694 165.662C194.066 165.373 194.44 165.086 194.817 164.803C195.675 164.155 196.56 163.506 197.456 162.874L197.84 162.606C198.109 162.421 198.377 162.235 198.645 162.054L198.888 161.89C199.367 161.565 199.853 161.248 200.343 160.939L200.586 160.786L200.827 160.636C201.069 160.486 201.309 160.339 201.548 160.196L201.787 160.053L202.265 159.775L202.734 159.506L202.829 159.454L203.2 159.25C203.355 159.166 203.509 159.085 203.663 159.006L203.892 158.888L204.115 158.776C204.193 158.739 204.27 158.7 204.346 158.663C204.848 158.415 205.36 158.187 205.88 157.979C206.021 157.919 206.161 157.865 206.3 157.818L206.71 157.674C206.833 157.633 206.953 157.594 207.068 157.559L207.108 157.547C207.17 157.527 207.232 157.509 207.293 157.493L207.311 157.488C207.439 157.451 207.566 157.419 207.691 157.389H207.7C208.054 157.304 208.414 157.243 208.777 157.206C208.944 157.189 209.111 157.18 209.279 157.181H209.363C209.475 157.181 209.583 157.188 209.69 157.199C209.739 157.199 209.788 157.209 209.836 157.215H209.856C209.904 157.221 209.952 157.228 210 157.239C210.047 157.248 210.095 157.256 210.141 157.267H210.156C210.203 157.277 210.245 157.289 210.294 157.303C210.548 157.374 210.79 157.484 211.012 157.628C211.121 157.699 211.223 157.779 211.317 157.868L211.344 157.894C211.362 157.91 211.379 157.927 211.395 157.944L211.444 157.997C211.846 158.418 212.178 158.901 212.428 159.427L212.466 159.517C212.551 159.717 212.618 159.924 212.666 160.135C212.808 160.781 212.753 161.455 212.508 162.07C212.415 162.318 212.302 162.557 212.169 162.785C211.858 163.309 211.489 163.796 211.07 164.237L210.981 164.332C210.848 164.472 210.71 164.612 210.565 164.752C210.501 164.815 210.434 164.877 210.367 164.94L210.162 165.129L210.055 165.224C209.797 165.454 209.532 165.677 209.263 165.893C209.1 166.025 208.936 166.154 208.77 166.281C208.184 166.729 207.587 167.161 206.979 167.578C206.612 167.83 206.242 168.077 205.869 168.321C204.95 168.924 204.021 169.512 203.083 170.084C201.115 171.294 198.934 172.588 196.609 173.995L196.007 174.36C195.348 174.762 194.726 175.146 194.14 175.512L193.845 175.697L193.287 176.055C192.917 176.292 192.548 176.531 192.179 176.77L191.882 176.966C191.737 177.06 191.593 177.156 191.449 177.252L191.308 177.342L190.876 177.633L190.647 177.79L190.379 177.976L190.13 178.149C189.713 178.444 189.325 178.725 188.968 178.992L188.834 179.094C188.624 179.253 188.416 179.415 188.211 179.58C187.902 179.829 187.62 180.067 187.367 180.296L187.243 180.409C187.172 180.474 187.102 180.539 187.035 180.603C186.989 180.648 186.946 180.693 186.898 180.736L186.834 180.8C186.691 180.944 186.551 181.091 186.416 181.242L186.35 181.318C186.203 181.488 186.075 181.651 185.963 181.81L185.913 181.881C185.825 182.009 185.744 182.141 185.671 182.277C185.652 182.311 185.635 182.345 185.618 182.379L185.569 182.481L185.536 182.555L185.515 182.605L185.498 182.65L185.475 182.711C185.413 182.88 185.37 183.056 185.345 183.234L185.337 183.296L185.331 183.354V183.669C185.331 183.695 185.331 183.721 185.338 183.749L185.343 183.797C185.343 183.823 185.349 183.848 185.353 183.876C185.357 183.902 185.364 183.949 185.372 183.986V183.991C185.379 184.026 185.386 184.06 185.395 184.095C185.404 184.13 185.413 184.17 185.424 184.206C185.443 184.277 185.467 184.347 185.492 184.417C185.508 184.459 185.523 184.5 185.54 184.541C185.54 184.549 185.546 184.558 185.55 184.566L185.586 184.647L185.636 184.758C185.69 184.873 185.749 184.985 185.813 185.094L185.879 185.208L185.947 185.322C185.959 185.341 185.973 185.359 185.988 185.376L186.01 185.399L186.035 185.422L186.061 185.442C186.099 185.469 186.14 185.49 186.183 185.505C186.206 185.513 186.23 185.519 186.254 185.525C186.831 185.655 188.017 185.178 189.593 184.346C189.682 184.298 189.78 184.248 189.875 184.196L190.355 183.934L190.589 183.804C190.756 183.715 190.926 183.614 191.1 183.515L191.417 183.336C193.5 182.137 195.988 180.597 198.56 179.093C198.801 178.952 199.043 178.811 199.285 178.672L199.771 178.361C200.335 178.038 200.902 177.719 201.471 177.404C202.188 177.01 202.91 176.626 203.639 176.254L204.115 176.013C204.431 175.857 204.744 175.705 205.053 175.557C205.651 175.273 206.256 175.003 206.868 174.748L207.203 174.612L207.243 174.596C209.018 173.893 210.627 173.459 211.929 173.459C212.21 173.456 212.492 173.48 212.769 173.528H212.778C212.867 173.544 212.948 173.562 213.031 173.582H213.046C213.259 173.636 213.466 173.713 213.662 173.812C213.937 173.954 214.184 174.143 214.393 174.371C214.489 174.477 214.574 174.592 214.649 174.714C214.789 174.929 214.899 175.162 214.978 175.406C215.01 175.501 215.038 175.594 215.067 175.693C215.278 176.45 215.257 177.253 215.007 177.998Z"
                                                        fill="#FF9D00" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                                        d="M203.21 123.685V123.194C203.21 81.34 169.292 47.411 127.435 47.411C85.5791 47.411 51.648 81.342 51.648 123.194V123.358C51.646 123.467 51.645 123.576 51.648 123.685C51.6529 123.848 51.6546 124.011 51.653 124.174L51.6581 124.534L51.661 124.663C51.661 124.723 51.6631 124.782 51.6651 124.842C51.6681 124.937 51.67 125.033 51.67 125.128L51.681 125.517L51.697 125.974L51.702 126.124L51.722 126.597V126.62C51.73 126.805 51.7401 126.989 51.7491 127.173L51.75 127.187C51.76 127.375 51.7701 127.564 51.7821 127.753C51.7921 127.927 51.802 128.101 51.815 128.275L51.8171 128.306C51.8258 128.455 51.8358 128.605 51.847 128.754L51.85 128.794L51.883 129.226L51.8861 129.254C51.8921 129.338 51.898 129.422 51.906 129.503C51.9658 130.224 52.0355 130.945 52.1151 131.664L52.12 131.709L52.181 132.238L52.2491 132.793L52.299 133.17L52.322 133.347C52.3753 133.755 52.433 134.162 52.495 134.568L52.4991 134.595L52.558 134.979C52.8435 136.808 53.1971 138.626 53.618 140.429L53.6231 140.451L53.655 140.586L53.746 140.971L53.802 140.904C56.002 138.274 59.158 136.824 62.689 136.824C65.519 136.824 68.4221 137.76 71.3321 139.605C73.2621 140.831 75.3961 143.002 77.5921 145.733C79.6241 142.911 82.4721 141.035 85.7301 140.523C86.3513 140.425 86.9792 140.376 87.6081 140.375C95.0441 140.375 99.523 146.828 101.215 152.633C102.051 154.594 106.08 163.526 112.156 169.568C121.392 178.795 123.703 188.316 119.132 198.511H119.148C119.459 198.546 119.772 198.578 120.087 198.607C120.274 198.625 120.46 198.643 120.648 198.659L120.714 198.665L121.127 198.7L121.507 198.73C121.869 198.758 122.232 198.784 122.596 198.807L122.885 198.824L123.114 198.838L123.256 198.846L123.703 198.869L123.825 198.874L124.294 198.895L124.816 198.915L125.235 198.927L125.305 198.929C125.394 198.933 125.483 198.936 125.572 198.936L125.668 198.939C126.258 198.953 126.847 198.96 127.437 198.959H128.063L128.51 198.954C128.62 198.949 128.729 198.949 128.84 198.949H129.014L129.165 198.945C129.224 198.943 129.283 198.941 129.343 198.941H129.522L129.873 198.932L130.401 198.914L130.982 198.888C131.15 198.882 131.316 198.873 131.482 198.865L131.661 198.854L131.927 198.84L132.083 198.831L132.201 198.823L132.738 198.788L133.274 198.749L133.761 198.71L134.103 198.681L134.479 198.647C135.107 198.591 135.733 198.525 136.359 198.45L136.786 198.399C132.287 188.247 134.616 178.767 143.813 169.577C149.876 163.519 153.905 154.587 154.745 152.625C156.438 146.821 160.914 140.368 168.352 140.368C168.981 140.368 169.61 140.418 170.231 140.516C173.486 141.028 176.334 142.904 178.369 145.726C180.566 142.996 182.699 140.823 184.63 139.597C187.539 137.753 190.445 136.817 193.272 136.817C196.388 136.817 199.212 137.947 201.345 140.02C201.384 139.851 201.422 139.682 201.459 139.512L201.568 139.006C201.607 138.821 201.646 138.636 201.683 138.451C201.749 138.124 201.815 137.797 201.878 137.467C201.944 137.125 202.007 136.781 202.067 136.437L202.098 136.251C202.117 136.141 202.135 136.031 202.156 135.92C202.19 135.748 202.218 135.576 202.246 135.402L202.257 135.336L202.328 134.883L202.398 134.424V134.42C202.449 134.081 202.497 133.742 202.542 133.403L202.553 133.319L202.616 132.841L202.667 132.433L202.757 131.629L202.792 131.306L202.801 131.218C202.82 131.044 202.838 130.87 202.854 130.696V130.682C202.867 130.544 202.881 130.405 202.893 130.266C202.964 129.478 203.024 128.686 203.072 127.891C203.081 127.761 203.088 127.63 203.096 127.499V127.493L203.122 127.002L203.128 126.892C203.144 126.56 203.158 126.228 203.169 125.896V125.884L203.174 125.754C203.179 125.645 203.183 125.535 203.183 125.425L203.185 125.381C203.189 125.278 203.193 125.172 203.193 125.067L203.196 124.977C203.199 124.872 203.202 124.768 203.202 124.663L203.204 124.574C203.207 124.441 203.21 124.307 203.21 124.174V123.685ZM108.638 199.391C114.64 190.59 114.214 183.984 105.98 175.754C97.7441 167.523 92.951 155.487 92.951 155.487C92.951 155.487 91.1621 148.496 87.0821 149.138C83.0021 149.78 80.0091 160.227 88.5521 166.622C97.0941 173.017 86.8521 177.353 83.5641 171.352C80.2761 165.35 71.299 149.923 66.645 146.972C61.991 144.021 58.718 145.675 59.815 151.757C60.36 154.776 65.4281 159.929 70.1631 164.743C74.9671 169.627 79.428 174.163 78.474 175.768C76.581 178.955 69.9141 172.023 69.9141 172.023C69.9141 172.023 49.038 153.025 44.494 157.976C40.304 162.539 46.765 166.418 56.7211 172.397C57.5671 172.905 58.4391 173.429 59.3321 173.969C70.7231 180.865 71.609 182.684 69.992 185.293C69.395 186.257 65.582 183.968 60.892 181.153C52.897 176.352 42.3551 170.023 40.8661 175.688C39.5781 180.591 47.334 183.595 54.368 186.32C60.228 188.59 65.5881 190.666 64.7991 193.484C63.9821 196.406 59.5531 193.969 54.7121 191.305C49.2771 188.314 43.3221 185.038 41.3731 188.735C37.6901 195.725 66.7831 203.954 67.0231 204.015C76.4231 206.453 100.295 211.619 108.638 199.391ZM147.303 199.391C141.301 190.59 141.727 183.984 149.962 175.754C158.197 167.523 162.99 155.487 162.99 155.487C162.99 155.487 164.779 148.496 168.859 149.138C172.939 149.78 175.932 160.227 167.39 166.622C158.847 173.017 169.089 177.353 172.377 171.352C175.666 165.35 184.637 149.923 189.291 146.972C193.945 144.021 197.22 145.675 196.122 151.757C195.578 154.776 190.509 159.929 185.774 164.744C180.97 169.628 176.509 174.163 177.462 175.768C179.355 178.955 186.027 172.019 186.027 172.019C186.027 172.019 206.902 153.022 211.448 157.973C215.637 162.535 209.176 166.415 199.219 172.394C198.348 172.917 197.478 173.441 196.609 173.966C185.218 180.862 184.332 182.681 185.948 185.289C186.546 186.254 190.359 183.964 195.048 181.149C203.044 176.349 213.586 170.019 215.075 175.685C216.364 180.588 208.607 183.592 201.573 186.317C195.713 188.587 190.353 190.663 191.141 193.481C191.957 196.402 196.385 193.965 201.225 191.301C206.66 188.31 212.616 185.032 214.564 188.732C218.248 195.726 189.15 203.947 188.915 204.007C179.515 206.453 155.643 211.619 147.303 199.391Z"
                                                        fill="#FFD21E" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                                        d="M152.047 102.567C153.229 102.985 154.108 104.257 154.944 105.468C156.074 107.104 157.126 108.627 158.74 107.769C160.644 106.756 162.205 105.202 163.225 103.302C164.246 101.402 164.681 99.2427 164.475 97.096C164.321 95.4908 163.813 93.9398 162.987 92.5548C162.161 91.1697 161.038 89.985 159.7 89.0862C158.361 88.1874 156.839 87.5968 155.245 87.3569C153.65 87.117 152.022 87.2339 150.478 87.699C148.934 88.1639 147.513 88.9653 146.316 90.0455C145.119 91.1257 144.176 92.4578 143.556 93.946C142.936 95.4342 142.653 97.0415 142.728 98.652C142.804 100.263 143.235 101.836 143.992 103.26C144.74 104.667 146.4 104.003 148.152 103.302C149.525 102.753 150.956 102.181 152.047 102.567ZM100.672 102.567C99.49 102.985 98.611 104.258 97.775 105.468C96.645 107.105 95.592 108.627 93.979 107.769C91.5845 106.501 89.7482 104.386 88.8278 101.838C87.9075 99.2895 87.9692 96.4896 89.0008 93.9841C90.0324 91.4786 91.9601 89.4471 94.408 88.2855C96.856 87.1239 99.6488 86.9156 102.242 87.701C104.307 88.3228 106.141 89.5427 107.513 91.2065C108.885 92.8704 109.732 94.9035 109.949 97.049C110.165 99.1945 109.74 101.356 108.728 103.26C107.979 104.667 106.319 104.003 104.567 103.303C103.193 102.753 101.764 102.181 100.672 102.567ZM144.099 149.318C152.242 142.903 155.233 132.429 155.233 125.977C155.233 120.877 151.802 122.482 146.309 125.202L145.999 125.355C140.957 127.852 134.245 131.177 126.877 131.177C119.508 131.177 112.796 127.852 107.755 125.354C102.084 122.545 98.527 120.783 98.527 125.978C98.527 132.634 101.709 143.563 110.443 149.912C111.596 147.573 113.219 145.497 115.211 143.813C117.202 142.129 119.52 140.874 122.018 140.126C122.89 139.866 123.788 141.367 124.707 142.904C125.594 144.386 126.501 145.902 127.423 145.902C128.406 145.902 129.371 144.408 130.314 142.95C131.299 141.425 132.26 139.94 133.189 140.237C137.864 141.738 141.775 144.993 144.099 149.318Z"
                                                        fill="#32343D" />
                                                    <path
                                                        d="M144.097 149.317C139.856 152.659 134.219 154.9 126.878 154.9C119.981 154.9 114.587 152.922 110.443 149.911C111.596 147.572 113.219 145.495 115.211 143.812C117.202 142.128 119.52 140.873 122.018 140.125C123.73 139.614 125.545 145.901 127.423 145.901C129.433 145.901 131.37 139.655 133.189 140.236C137.863 141.738 141.773 144.993 144.097 149.317Z"
                                                        fill="#FF323D" />
                                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                                        d="M81.2 111.64C80.2312 112.288 79.1173 112.687 77.9572 112.801C76.7971 112.916 75.6267 112.742 74.55 112.295C73.6893 111.94 72.9072 111.418 72.2488 110.759C71.5903 110.101 71.0684 109.319 70.713 108.458C70.267 107.381 70.0935 106.211 70.2082 105.051C70.3228 103.891 70.7219 102.777 71.37 101.808C72.1488 100.642 73.2558 99.7333 74.5512 99.1967C75.8466 98.6601 77.272 98.5197 78.6471 98.7935C80.0223 99.0672 81.2853 99.7427 82.2764 100.734C83.2675 101.726 83.9422 102.99 84.215 104.365C84.4883 105.74 84.3477 107.165 83.8113 108.46C83.2748 109.755 82.3654 110.861 81.2 111.64ZM182.613 111.64C181.644 112.288 180.53 112.687 179.37 112.801C178.209 112.916 177.039 112.742 175.962 112.295C175.101 111.939 174.319 111.418 173.661 110.759C173.003 110.101 172.481 109.319 172.125 108.458C171.68 107.381 171.507 106.211 171.621 105.051C171.736 103.891 172.135 102.777 172.782 101.808C173.364 100.936 174.133 100.205 175.032 99.6658C175.931 99.1269 176.938 98.7942 177.981 98.6917C179.025 98.5891 180.078 98.7193 181.064 99.0728C182.051 99.4264 182.947 99.9944 183.688 100.736C184.68 101.727 185.355 102.99 185.628 104.365C185.902 105.74 185.761 107.165 185.224 108.46C184.687 109.755 183.779 110.861 182.613 111.64Z"
                                                        fill="#FFAD03" />
                                                </svg>
                                                HuggingFace
                                            </div>
                                        </label>
                                    </div>
                                    <TextInput id="huggingface_username" v-model="form.huggingface_username"
                                        placeholder="Your username" class="w-full" />
                                </div>
                                <div>
                                    <div class="field-label-container">

                                        <label class="input-label" for="homepage">Homepage</label>
                                    </div>
                                    <TextInput id="homepage" v-model="form.homepage" placeholder="Your website URL"
                                        class="w-full" />
                                </div>
                            </div>

                            <div class="form-group">
                            
                                    <label class="input-label" for="country">Base of Operations</label>
                                    <p class="text-xs 2xl:text-sm text-slate-400 mb-2">Which country do you primarily
                                        operate in?</p>
                                    <InputError :message="baseOfOperationsError" />
                                
                                <TextInput id="country" type="text" v-model="form.base_of_operations"
                                    placeholder="United States, Germany, etc.">
                                </TextInput>
                            </div>
                        </div>
                    </div>

                    <div class="settings-card">
                        <h3>Security</h3>
                        <div class="form-group">
                            <div class="space-y-4">
                                <div class="form-group">
                                    <div class="field-label-container">
                                        <label class="input-label" for="current_password">Current Password</label>
                                        <InputError :message="currentPasswordError" />
                                    </div>
                                    <TextInput id="current_password" v-model="form.current_password" type="password"
                                        placeholder="Enter your current password" />
                                </div>

                                <div class="form-group">
                                    <div class="field-label-container">
                                        <label class="input-label" for="new_password">New Password</label>
                                        <InputError :message="newPasswordError" />
                                    </div>
                                    <TextInput id="new_password" v-model="form.new_password" type="password"
                                        placeholder="Enter your new password" />
                                </div>

                                <div class="form-group">
                                    <div class="field-label-container">
                                        <label class="input-label" for="new_password_confirmation">Confirm New
                                            Password</label>
                                        <InputError :message="newPasswordConfirmationError" />
                                    </div>
                                    <TextInput id="new_password_confirmation" v-model="form.new_password_confirmation"
                                        type="password" placeholder="Confirm your new password" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="settings-card" v-if="form.onboarding_fields && Object.keys(form.onboarding_fields).length > 0">
                        <h3>Onboarding Fields</h3>
                        <div class="form-group">
                            <div class="grid grid-cols-1 gap-4">
                                <div v-for="(value, key) in form.onboarding_fields" :key="key">
                                    <div class="field-label-container">
                                        <label class="input-label mb-0" :for="`onboarding_${key}`">{{ key }}</label>
                                    </div>
                                    <TextInput 
                                        :id="`onboarding_${key}`" 
                                        :model-value="form.onboarding_fields[key]"
                                        @update:model-value="form.onboarding_fields[key] = $event"
                                        type="text"
                                        :placeholder="`Enter ${key}`" 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div class="form-actions flex justify-end">
                    <PrimaryButton type="submit" :disabled="loading" class="save-changes-btn">
                        <ButtonLoading v-if="loading" size="sm" text="Saving..." />
                        <template v-else>Save Changes</template>
                    </PrimaryButton>
                </div>
            </form>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.creator-profile {
    @apply min-h-screen;
}

.creator-profile__hero {
    @apply relative;
    background-size: 100% 100%;
    background-position: 0px 0px;
    background-repeat: no-repeat;
}






.creator-profile__container {
    @apply relative container px-0;
}

.creator-profile__header {
    @apply flex flex-col md:flex-row gap-8 items-center;
}

.creator-profile__avatar-container {
    @apply relative flex-shrink-0 text-center;
}

.profile-photo {
    @apply relative mx-auto w-32 h-32;
}

.profile-photo__actions {
    @apply absolute -bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2;
}

.profile-photo__upload-btn {
    @apply flex items-center justify-center w-9 p-2 h-9 rounded-full bg-echoes-primary hover:bg-echoes-primary/90 text-white shadow-lg cursor-pointer transition ease-in-out;
}

.profile-photo__remove-btn {
    @apply bg-red-600 hover:bg-red-700/90 p-2.5 transition ease-in-out;
}

.profile-image-preview {
    @apply w-32 h-32 rounded-full flex items-center justify-center relative overflow-hidden bg-echoes-background-black border-2 border-echoes-border-gray;
}

.profile-image-preview.has-image {
    @apply border-echoes-primary;
}

.preview-image {
    @apply w-full h-full object-cover;
}

.default-avatar {
    @apply w-16 h-16 text-white/80;
}

.profile-photo__help {
    @apply mt-8 text-sm text-white/80 max-w-[200px];
}

.creator-profile__info {
    @apply flex-1;
}

.creator-profile__name-row {
    @apply flex flex-col items-center md:items-start md:flex-row md:justify-between gap-4;
}

.creator-profile__title {
    @apply text-3xl font-bold text-center md:text-left bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit tracking-tight;
}

.creator-profile__subtitle {
    @apply mt-2 text-white/80 text-center md:text-left;
}

.settings-form {
    @apply p-5 space-y-6 pb-8;
}

.settings-form__grid {
    @apply grid grid-cols-1 md:grid-cols-2 gap-6;
}

.settings-card {
    @apply bg-echoes-background-black rounded-[21px] px-6 py-5 border border-echoes-border-gray;
    
}

.settings-card .form-group {
    @apply space-y-1;
}

.settings-card h3 {
    @apply text-lg font-semibold mb-4 text-white/80 relative pb-4;
}

.settings-card h3::after {
    content: '';
    @apply absolute bottom-0 left-0 right-0 h-px bg-echoes-background-gray;
}

.input-label {
    @apply block text-sm font-medium text-white/80;
}

.field-label-container {
    @apply flex items-center mb-2 w-fit gap-2;
}

.text-area__field {
    @apply w-full h-full px-4 py-3 text-sm bg-echoes-background-black text-white border border-echoes-border-gray rounded-[13px] placeholder-white/70 focus:outline-none  focus:border-echoes-primary resize-none transition-all duration-200;

    &::placeholder {
        @apply transition-all duration-200 text-white/50 hover:text-white/80;
    }

    &:hover {
        @apply bg-echoes-background-black
        border-white/20
        text-white
        ;
    }

    &:focus::placeholder {
        @apply opacity-0 translate-x-1 border-echoes-primary;
    }

    &:focus {
        @apply border-echoes-primary;
    }

    &:disabled {
        @apply cursor-not-allowed opacity-60 bg-echoes-background-gray/30;
    }
}

.required-mark {
    @apply text-red-600 ml-1;
}

.form-group {
    @apply relative;
}

.settings-notification {
    @apply bg-echoes-background-black rounded-xl p-6 border border-echoes-border-gray mb-6;
}

.settings-notification--warning {
    @apply border-amber-500/30 bg-amber-500/5;
}

.settings-notification--success {
    @apply border-emerald-500/30 bg-emerald-500/5;
}

.btn--secondary {
    @apply px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 bg-echoes-background-black hover:bg-echoes-background-black/50 text-white border border-echoes-border-gray hover:border-echoes-border-gray shadow-sm hover:shadow-md;
}

.save-changes-btn {
    @apply px-6 2xl:px-8 py-2 text-sm  bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center min-w-[160px] disabled:opacity-70 disabled:cursor-not-allowed disabled:from-green-500/60 disabled:to-green-700/60;
}

.cropper-modal {
    @apply fixed inset-0 z-50 flex items-center justify-center bg-echoes-background-black/50 p-2;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
}

.cropper-container {
    @apply overflow-hidden bg-echoes-background-black rounded-xl shadow-xl w-full max-h-[85vh] mt-10 flex flex-col;
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
    max-width: 540px;
}

.modal__header {
    @apply sticky top-0 z-10 flex items-center justify-between p-4 border-b border-echoes-border-gray bg-echoes-background-black;
}

.modal__title {
    @apply text-xl font-semibold text-white/80;
}

.modal__close {
    @apply text-white/80 hover:text-echoes-primary p-1 rounded-full transition-colors;
}

.modal__body {
    @apply p-4 overflow-y-auto;
    flex: 1;
}

.modal__footer {
    @apply sticky bottom-0 z-10 p-4 border-t border-echoes-border-gray flex items-center justify-end gap-3 bg-echoes-background-black;
}

.btn {
    @apply px-4 py-1.5 rounded-lg text-xs 2xl:text-sm font-medium transition-colors;
}

.cropper {
    @apply bg-zinc-900;
}

.handler-default {
    @apply w-3 h-3 bg-echoes-primary border border-white rounded-full;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.form-heading {
    @apply text-2xl font-bold text-white mb-6;
}
</style>
